import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BisacCode } from "reedy-data/api";
import type { FirestoreProductBisacSubject } from "reedy-data/models";
import { useAnalytics } from "~/contexts/analytics-context";
import { useProduct } from "~/contexts/product-context";
import { useProductActions } from "~/hooks/use-product-actions";
import { toast } from "~/utils/toast";
import {
  addFinalBisacCodes,
  addFinalBisacSubject,
  getDatabaseBisacSubjects,
  getFinalBisacSubjects,
  normalizeBisacCode,
  removeFinalBisacSubject,
  reorderFinalBisacSubjects,
  toManualSubject,
} from "~/shared/bisac/product-bisac-subjects";

const BISAC_SAVE_DELAY_MS = 400;

type BisacSubjectsUpdate = (
  subjects: FirestoreProductBisacSubject[],
) => FirestoreProductBisacSubject[];

interface PendingChange {
  update: BisacSubjectsUpdate;
  onSaved: () => void;
}

const applyChanges = (
  changes: PendingChange[],
  subjects: FirestoreProductBisacSubject[],
) => changes.reduce((list, { update }) => update(list), subjects);

interface UnsavedSubjects {
  subjects: FirestoreProductBisacSubject[];
  storedBeforeSave?: FirestoreProductBisacSubject[];
  isSaved: boolean;
}

export const useBisacSubjects = () => {
  const { t } = useTranslation(["productDetails"]);
  const { product } = useProduct();
  const { updateBisacSubjects } = useProductActions();
  const { gaEvent } = useAnalytics();
  const storedSubjects = product?.subjects?.bisac;
  const [unsaved, setUnsaved] = useState<UnsavedSubjects>();

  const isShowingUnsaved =
    unsaved &&
    (!unsaved.isSaved || unsaved.storedBeforeSave === storedSubjects);
  const subjects = (isShowingUnsaved ? unsaved.subjects : storedSubjects) ?? [];
  const finalSubjects = getFinalBisacSubjects(subjects);

  const productId = product?.id;
  const subjectsRef = useRef(subjects);
  const storedRef = useRef(storedSubjects);
  const pendingRef = useRef<PendingChange[]>([]);
  const isSavingRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const savePendingRef = useRef(async () => {});
  const scheduleSave = () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(
      () => savePendingRef.current(),
      BISAC_SAVE_DELAY_MS,
    );
  };

  const savePending = async () => {
    if (!productId || isSavingRef.current || !pendingRef.current.length) {
      return;
    }
    const batch = pendingRef.current;
    pendingRef.current = [];
    isSavingRef.current = true;
    let written: FirestoreProductBisacSubject[] | undefined;

    try {
      await updateBisacSubjects(productId, (stored) => {
        written = applyChanges(batch, stored);
        return written;
      });
      batch.forEach(({ onSaved }) => onSaved());
      if (!pendingRef.current.length) {
        setUnsaved((current) => ({
          subjects: written ?? current?.subjects ?? [],
          storedBeforeSave: storedRef.current,
          isSaved: true,
        }));
      }
    } catch {
      const unsavedAfterFailure = applyChanges(
        pendingRef.current,
        storedRef.current ?? [],
      );
      subjectsRef.current = unsavedAfterFailure;
      setUnsaved(
        pendingRef.current.length
          ? { subjects: unsavedAfterFailure, isSaved: false }
          : undefined,
      );
      toast.error(t("productDetails:errorToast.errorUpdate"));
    } finally {
      isSavingRef.current = false;
      if (pendingRef.current.length) scheduleSave();
    }
  };

  useEffect(() => {
    subjectsRef.current = subjects;
    storedRef.current = storedSubjects;
    savePendingRef.current = savePending;
  });

  useEffect(() => {
    const saveNow = () => {
      clearTimeout(saveTimerRef.current);
      savePendingRef.current();
    };
    window.addEventListener("pagehide", saveNow);
    return () => {
      window.removeEventListener("pagehide", saveNow);
      saveNow();
    };
  }, []);

  const change = (update: BisacSubjectsUpdate, onSaved: () => void) => {
    const next = update(subjectsRef.current);
    subjectsRef.current = next;
    setUnsaved({ subjects: next, isSaved: false });
    pendingRef.current.push({ update, onSaved });
    scheduleSave();
  };

  const logAdded = (codes: string[], origin: "database" | "browse") => {
    if (!productId) return;
    codes.forEach((code) =>
      gaEvent({
        type: "add_bisac_code",
        payload: { book: productId, code, origin },
      }),
    );
  };

  const addCodes = (codes: BisacCode[]) => {
    const finalCodes = new Set(
      getFinalBisacSubjects(subjectsRef.current).map(({ subjectCode }) =>
        normalizeBisacCode(subjectCode),
      ),
    );
    const added = [...new Set(codes.map(({ code }) => code))].filter(
      (code) => !finalCodes.has(normalizeBisacCode(code)),
    );
    if (!added.length) return 0;
    const newSubjects = new Map(
      codes.map((code) => [code.code, toManualSubject(code)]),
    );
    change(
      (stored) =>
        addFinalBisacCodes(
          stored,
          codes,
          (code) => newSubjects.get(code.code)!,
        ),
      () => logAdded(added, "browse"),
    );
    return added.length;
  };

  const addSubject = ({ id, subjectCode }: FirestoreProductBisacSubject) =>
    change(
      (stored) => addFinalBisacSubject(stored, id),
      () => logAdded([subjectCode ?? ""], "database"),
    );

  const removeSubject = ({ id, subjectCode }: FirestoreProductBisacSubject) =>
    change(
      (stored) => removeFinalBisacSubject(stored, id),
      () => {
        if (!productId) return;
        gaEvent({
          type: "remove_bisac_code",
          payload: { book: productId, code: subjectCode ?? "" },
        });
      },
    );

  const reorder = (orderedIds: string[]) =>
    change(
      (stored) => reorderFinalBisacSubjects(stored, orderedIds),
      () => {},
    );

  return {
    databaseSubjects: getDatabaseBisacSubjects(subjects),
    finalSubjects,
    isFinal: (subject: FirestoreProductBisacSubject) =>
      finalSubjects.some(({ id }) => id === subject.id),
    addCodes,
    addSubject,
    removeSubject,
    reorder,
  };
};
